/**
 * useGeoLocale — Prioritet 5: Nordisk geo-auto-routing
 * ----------------------------------------------------
 * Consolidates locale-detection signals into a deterministic order:
 *
 *   1. Explicit user choice (localStorage `td_lang_pref`) — never overridden.
 *   2. Domain TLD (relark.dk → da, .se → sv, .de → de, .app → en).
 *   3. Browser `navigator.language` — fallback for non-ccTLD hosts.
 *
 * The hook also exposes `mismatch` so the soft `LocaleBanner` can offer
 * the visitor a one-click switch to their preferred locale without forcing
 * a hard cross-domain redirect (SEO-safe, hreflang remains canonical).
 *
 * Analytics:
 *   - `geo_locale_auto_set` — fired once per session when TLD/browser sets
 *     a locale that differs from the previously active one.
 *   - `geo_locale_user_override` — fired when the visitor explicitly picks
 *     a different language via the banner or switcher.
 */

import { useEffect, useMemo } from "react";
import i18n from "@/i18n/config";
import {
  getDomainConfig,
  ORIGIN_BY_LANG,
  urlFor,
} from "@/lib/domain-config";
import { SUPPORTED_SEO_LANGS, SeoLang } from "@/lib/seo-config";
import { trackEvent } from "@/lib/analytics";

const LANG_PREF_KEY = "td_lang_pref";
const GEO_AUTO_SET_KEY = "td_geo_auto_set"; // session — fire-once

function detectBrowserLang(): SeoLang | null {
  try {
    const raw = (navigator.language || "").toLowerCase();
    const short = raw.split("-")[0];
    if (short === "pt") return "pt";
    if (short === "nn") return "nb";
    if ((SUPPORTED_SEO_LANGS as readonly string[]).includes(short)) {
      return short as SeoLang;
    }
  } catch {}
  return null;
}

function readPref(): SeoLang | null {
  try {
    const v = localStorage.getItem(LANG_PREF_KEY);
    if (v && (SUPPORTED_SEO_LANGS as readonly string[]).includes(v)) {
      return v as SeoLang;
    }
  } catch {}
  return null;
}

export interface GeoLocaleState {
  /** The locale we believe the user wants right now (TLD wins, then pref, then browser). */
  active: SeoLang;
  /** The locale the browser advertises (navigator.language → short code). */
  browser: SeoLang | null;
  /** True when an explicit user choice exists in localStorage. */
  hasUserPref: boolean;
  /**
   * True when the active locale (forced by TLD) differs from the browser
   * locale AND the user hasn't made an explicit choice yet. The banner
   * uses this to offer a soft switch.
   */
  mismatch: boolean;
  /** The locale to suggest in the banner — usually `browser`. */
  suggested: SeoLang | null;
  /** Origin of the suggested locale's canonical site. */
  suggestedOrigin: string | null;
}

export function useGeoLocale(): GeoLocaleState {
  const domain = getDomainConfig();
  const pref = readPref();
  const browser = detectBrowserLang();

  const active: SeoLang = useMemo(() => {
    // 1. User pref always wins.
    if (pref) return pref;
    // 2. ccTLD encodes the locale.
    if (domain.role === "ccTLD") return domain.defaultLang;
    // 3. Browser language on international/dev hosts.
    return browser ?? domain.defaultLang;
  }, [pref, domain.role, domain.defaultLang, browser]);

  // Sync i18n + <html lang> exactly once per `active` change.
  useEffect(() => {
    if (i18n.language !== active) {
      i18n.changeLanguage(active);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = active;
    }
    // Fire-once analytics signal so we can measure how often TLD/browser
    // overrides the previously stored locale (helps tune the banner).
    try {
      const flag = sessionStorage.getItem(GEO_AUTO_SET_KEY);
      if (!flag) {
        sessionStorage.setItem(GEO_AUTO_SET_KEY, active);
        trackEvent("geo_locale_auto_set", {
          locale: active,
          domain_role: domain.role,
          hostname: domain.hostname,
          browser_lang: browser ?? null,
          had_pref: !!pref,
        });
      }
    } catch {}
  }, [active, domain.role, domain.hostname, browser, pref]);

  const mismatch =
    !pref &&
    domain.role === "ccTLD" &&
    !!browser &&
    browser !== domain.defaultLang &&
    !!ORIGIN_BY_LANG[browser];

  return {
    active,
    browser,
    hasUserPref: !!pref,
    mismatch,
    suggested: mismatch ? browser : null,
    suggestedOrigin: mismatch && browser ? ORIGIN_BY_LANG[browser] : null,
  };
}

/**
 * Persist a deliberate user choice and emit the override event so we can
 * track how often visitors disagree with our auto-detection.
 */
export function setGeoLocalePreference(lang: SeoLang, source: string) {
  try {
    localStorage.setItem(LANG_PREF_KEY, lang);
  } catch {}
  trackEvent("geo_locale_user_override", { locale: lang, source });
}

export { urlFor };